import styles from "./Map.module.css";
import { useGetDataPDF } from "@/api/generate-hpdb";
import { P } from "@/components/ui/typography";
import { todayMonth } from "@/utils/helper/formatDate";
import { MapImage } from "./MapImage";
import { MapSummary } from "./MapSummary";
import CompassImage from "@/assets/compass.png";
import LinknetLogo from "@/assets/linknet-logo.png";
import { useParams } from "react-router-dom";
import { MISSING_ID_IN_ROUTE } from "@/utils/constant";

type CableType = {
  color: string;
  label: string;
  type: "aerial" | "underground";
};

const cableTypes: CableType[] = [
  { label: "FIBER 12/2T AERIAL", type: "aerial", color: "#EF4444" },
  { label: "FIBER 12/2T UG", type: "underground", color: "#EF4444" },
  { label: "FIBER 24/4T AERIAL", type: "aerial", color: "#3B82F6" },
  { label: "FIBER 24/4T UG", type: "underground", color: "#3B82F6" },
];

export function MapIndex() {
  const { id } = useParams();
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }

  const { data: dataPDF } = useGetDataPDF({
    rfsId: id,
  });

  const baseURL = `${window.location.protocol}//${window.location.host}`;

  const descriptions = dataPDF?.descriptions ?? [];
  const symbolDescriptions = dataPDF?.symbolDescriptions ?? [];

  const combinedData = descriptions.map((desc) => {
    const matchingSymbol = symbolDescriptions.find(
      (symbol) => symbol.paramTag === desc.paramTag,
    );
    return {
      value: desc.value,
      symbol: matchingSymbol?.value ?? null,
    };
  });

  const leftColumn = combinedData.slice(0, 5);
  const rightColumn = combinedData.slice(5);
  const emptyDivCount = leftColumn.length - rightColumn.length;

  return (
    <div className={styles.map}>
      <div className="flex h-screen gap-2">
        <div className="flex flex-1 flex-col items-start">
          <MapImage />
          <MapSummary />
        </div>

        <div className="h-full w-[168px] border-b-0 border-gray-200 text-[4px]">
          {/* Linknet Logo */}
          <div className="flex w-full justify-center border border-b-0">
            <img src={LinknetLogo} alt="Linket Logo" className="h-16" />
          </div>

          {/* location */}
          <div className="border border-t-0 border-b-gray-200 text-left">
            <div className="px-1 pb-1">
              <P className="text-xs">PT. LINKNET</P>
              <P>LIPPO CYBERPARK, BULEVARD GAJAH MADA NO 2170</P>
              <P>LIPPO VILLAGE - TANGERANG 15811 - INDONESIA</P>
              <P>TEL. (021) 55777755 FAX. (021) 55777373</P>
            </div>
          </div>

          {/* copyright */}
          <div className="border border-t-0 border-b-gray-200 p-1 text-left">
            <P className="text-[6px]">COPYRIGHT</P>
            <P>
              No part of this drawing may by reproduced, transmitted or recorded
              in any form or otherwise without the prior writen permission of
              System & Network Design Division
            </P>
          </div>

          {/* description */}
          <div className="border border-t-0 border-b-gray-200 text-center">
            <P className="p-1 text-[6px]">DESCRIPTION</P>
            <div className="grid grid-cols-2">
              <div>
                {leftColumn?.map((item, index) => (
                  <div
                    key={`left-${index}`}
                    className="flex h-4 items-center gap-1 border border-b-0 border-l-0 border-gray-200 p-1 text-left"
                  >
                    <img
                      className="h-2 w-2"
                      src={`${baseURL}/${item.symbol}`}
                      alt={`${item.value}`}
                    />
                    <P>{item.value}</P>
                  </div>
                ))}
              </div>
              <div>
                {rightColumn?.map((item, index) => (
                  <div
                    key={`right-${index}`}
                    className="flex h-4 items-center gap-1 border border-b-0 border-l-0 border-r-0 border-gray-200 p-1 text-left"
                  >
                    <img
                      className="h-2 w-2"
                      src={`${baseURL}/${item.symbol}`}
                      alt={`${item.value}`}
                    />
                    <P>{item.value}</P>
                  </div>
                ))}
                {Array.from({ length: emptyDivCount }).map((_, index) => (
                  <div
                    key={`empty-${index}`}
                    className="flex h-4 items-center gap-1 border border-b-0 border-l-0 border-r-0 border-gray-200 p-1"
                  ></div>
                ))}
              </div>
            </div>
          </div>

          {/* cable type */}
          <div className="border border-r-0 border-t-0 border-b-gray-200 text-center">
            <P className="border border-b-0 border-l-0 border-t-0 border-r-gray-200 p-1 text-[6px]">
              CABLE TYPE
            </P>
            <div className="grid grid-cols-2">
              {cableTypes.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between border border-b-0 border-l-0 border-gray-200 p-1"
                >
                  <span>{item.label}</span>
                  <div
                    className={`h-1 w-8 ${
                      item.type === "aerial" ? "border-solid" : "border-dashed"
                    } border-t-2 border-blue-50`}
                    style={{ borderColor: item.color }}
                  ></div>
                </div>
              ))}
            </div>
          </div>

          {/* kode proyek */}
          <div className="border border-t-0 border-b-gray-200">
            <div className="w-fit p-1">
              <P className="border border-l-0 border-r-0 border-t-0 border-b-gray-200 pb-[1px]">
                Kode Proyek
              </P>
              <P className="pt-[1px]">Project Code</P>
            </div>

            <P className="mb-1 text-center text-[6px]">
              {dataPDF?.projectCode ?? "-"}
            </P>
          </div>

          {/* nama proyek */}
          <div className="border border-t-0 border-b-gray-200">
            <div className="w-fit p-1">
              <P className="border border-l-0 border-r-0 border-t-0 border-b-gray-200 pb-[1px]">
                Nama Proyek
              </P>
              <P className="pt-[1px]">Project Name</P>
            </div>

            <P className="mb-1 text-center text-[6px]">
              {dataPDF?.clusterName} {"RT " + dataPDF?.rt}{" "}
              {dataPDF?.rt && dataPDF?.rw
                ? ` & RW ${dataPDF.rw}`
                : "RW " + dataPDF?.rw}{" "}
              <br /> {dataPDF?.district}
            </P>
          </div>

          {/* tipe gambar */}
          <div className="border border-t-0 border-b-gray-200">
            <div className="w-fit p-1">
              <P className="border border-l-0 border-r-0 border-t-0 border-b-gray-200 pb-[1px]">
                Tipe Gambar
              </P>
              <P className="pt-[1px]">Drawing Type</P>
            </div>

            <P className="mb-1 text-center text-[6px]">AS BUILT DRAWING</P>
          </div>

          {/* digambar */}
          <div className="border border-t-0 border-b-gray-200">
            <P className="p-1 text-center text-[6px]">DRAWING APPROVAL</P>
          </div>

          {/* diperiksa */}
          <div className="border border-t-0 border-b-gray-200">
            <div className="flex justify-between p-1">
              <div className="w-fit">
                <P className="border border-l-0 border-r-0 border-t-0 border-b-gray-200 pb-[1px]">
                  Digambar
                </P>
                <P className="pt-[1px]">Drawn</P>
              </div>

              <div className="w-fit">
                <P className="border border-l-0 border-r-0 border-t-0 border-b-gray-200 pb-[1px] text-right">
                  Tanggal
                </P>
                <P className="pt-[1px] text-right">Date</P>
              </div>
            </div>

            <div className="mt-4 flex justify-between p-1">
              <P>{dataPDF?.drawn ?? "-"}</P>
              <P>{todayMonth()}</P>
            </div>
          </div>

          {/* diperiksa */}
          <div className="border border-t-0 border-b-gray-200">
            <div className="flex justify-between p-1">
              <div className="w-fit">
                <P className="border border-l-0 border-r-0 border-t-0 border-b-gray-200 pb-[1px]">
                  Diperiksa
                </P>
                <P className="pt-[2px]">Checked</P>
              </div>

              <div className="w-fit">
                <P className="border border-l-0 border-r-0 border-t-0 border-b-gray-200 pb-[1px] text-right">
                  Tanggal
                </P>
                <P className="pt-[1px] text-right">Date</P>
              </div>
            </div>

            <div className="mt-4 flex justify-between p-1">
              <P>{dataPDF?.checked ?? "-"}</P>
              <P>{todayMonth()}</P>
            </div>
          </div>

          {/* disetujui */}
          <div className="border border-t-0 border-b-gray-200">
            <div className="flex justify-between p-1">
              <div className="w-fit">
                <P className="border border-l-0 border-r-0 border-t-0 border-b-gray-200 pb-[1px]">
                  Disetujui
                </P>
                <P className="pt-[1px]">Approved</P>
              </div>

              <div className="w-fit">
                <P className="border border-l-0 border-r-0 border-t-0 border-b-gray-200 pb-[1px] text-right">
                  Tanggal
                </P>
                <P className="pt-[1px] text-right">Date</P>
              </div>
            </div>

            <div className="mt-4 flex justify-between p-1">
              <P>{dataPDF?.approved ?? "-"}</P>
              <P>{todayMonth()}</P>
            </div>
          </div>

          {/* disetujui */}
          <div className="border border-t-0 border-b-gray-200">
            <div className="flex justify-between p-1">
              <div className="w-fit">
                <P className="border border-l-0 border-r-0 border-t-0 border-b-gray-200 pb-[1px]">
                  Disetujui
                </P>
                <P className="pt-[1px]">Approved</P>
              </div>

              <div className="w-fit">
                <P className="border border-l-0 border-r-0 border-t-0 border-b-gray-200 pb-[1px] text-right">
                  Tanggal
                </P>
                <P className="pt-[1px] text-right">Date</P>
              </div>
            </div>

            <div className="mt-4 flex justify-between p-1">
              <P>{dataPDF?.approvedTenant ?? "-"}</P>
              <P>{todayMonth()}</P>
            </div>
          </div>

          {/* referensi */}
          <div className="flex justify-between">
            <div className="w-full border border-t-0 border-b-gray-200">
              <div className="w-fit p-1">
                <P className="border border-l-0 border-r-0 border-t-0 border-b-gray-200 pb-[1px]">
                  ID FDT/Referensi
                </P>
                <P className="pt-[1px]">FDT ID/REFERENCE</P>
              </div>

              <P className="mb-1 text-center text-[6px]">
                {dataPDF?.fdtId ?? "-"}
              </P>
            </div>
            <div className="w-full border border-l-0 border-t-0 border-b-gray-200">
              <div className="w-fit p-1">
                <P className="border border-l-0 border-r-0 border-t-0 border-b-gray-200 pb-[1px]">
                  Revisi
                </P>
                <P className="pt-[1px]">Revision</P>
              </div>

              <P className="mb-1 text-center text-[6px]">R - 0</P>
            </div>
          </div>

          {/* peta */}
          <div className="flex justify-between">
            <div className="w-full border border-t-0 border-b-gray-200">
              <div className="w-fit p-1">
                <P className="border border-l-0 border-r-0 border-t-0 border-b-gray-200 pb-[1px]">
                  Ref. Peta
                </P>
                <P className="pt-[1px]">Map Ref.</P>
              </div>

              {/* <P className="mb-1 text-center"></P> */}
            </div>
            <div className="w-full border border-l-0 border-t-0 border-b-gray-200">
              <div className="w-fit p-1">
                <P className="border border-l-0 border-r-0 border-t-0 border-b-gray-200 pb-[1px]">
                  No. Drawing
                </P>
                <P className="pt-[1px]">Drawing No.</P>
              </div>

              {/* <P className="mb-2 text-center"></P> */}
            </div>
          </div>

          {/* direction */}
          <div className="grid grid-cols-2 border border-t-0 border-gray-200">
            <div className="border border-b-0 border-l-0 border-t-0 border-gray-200">
              <div className="w-fit p-1">
                <P className="border border-l-0 border-r-0 border-t-0 border-b-gray-200 pb-[1px]">
                  Arah
                </P>
                <P className="pt-[1px]">Direction</P>
              </div>

              <div className="flex justify-center">
                <img
                  src={CompassImage}
                  alt="Compass"
                  className="mb-1 h-[35px] w-[34px]"
                />
              </div>
            </div>

            <div>
              <div className="w-full border border-l-0 border-r-0 border-t-0 border-b-gray-200">
                <div className="w-fit p-1">
                  <P className="border border-l-0 border-r-0 border-t-0 border-b-gray-200 pb-[1px]">
                    Skala
                  </P>
                  <P className="pt-[1px]">Scale</P>
                </div>

                <P className="mb-1 text-center text-[6px]">N.T.S</P>
              </div>

              <div className="w-full border border-b-0 border-l-0 border-r-0 border-t-0 border-b-gray-200">
                <div className="w-fit p-1">
                  <P className="border border-l-0 border-r-0 border-t-0 border-b-gray-200 pb-[1px]">
                    Halaman
                  </P>
                  <P className="pt-[1px]">Page</P>
                </div>

                <P className="mb-1 text-center text-[6px]">1 OF 1</P>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
